import { Injectable } from '@angular/core';

/**
 * State a visitor needs to carry across the Stripe redirect so the success
 * page can act on their behalf, and so a repeated Pay click reuses the same
 * draft instead of creating duplicates.
 *
 * Used by both branches that go through checkout:
 * - Anonymous guest: `guestToken` / `guestUserId` are populated (the backend
 *   auto-creates a guest user) and the success page shows the "claim your
 *   account" CTA.
 * - Logged-in user publishing: `guestToken` / `guestUserId` are null — the
 *   real bearer authenticates the calls and no claim CTA is shown.
 *
 * Persisted in `sessionStorage` (not `localStorage`): it should survive the
 * round-trip to Stripe and back, but not leak into a brand-new browser session.
 */
export interface PendingCheckoutState {
  propertyListingId: string;
  /** JWT for the auto-created guest user. Null for logged-in users. */
  guestToken: string | null;
  /** Id of the auto-created guest user. Null for logged-in users. */
  guestUserId: string | null;
  /** The customer's email — `contactPersonEmail` from the wizard. */
  customerEmail: string;
}

@Injectable({
  providedIn: 'root',
})
export class PendingCheckoutService {
  private static readonly KEY = 'pending-checkout-state';

  save(state: PendingCheckoutState): void {
    try {
      sessionStorage.setItem(PendingCheckoutService.KEY, JSON.stringify(state));
    } catch {
      // Storage may be unavailable (private mode / quota) — non-fatal.
    }
  }

  get(): PendingCheckoutState | null {
    try {
      const raw = sessionStorage.getItem(PendingCheckoutService.KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as PendingCheckoutState | null;
      return parsed?.propertyListingId ? parsed : null;
    } catch {
      return null;
    }
  }

  clear(): void {
    try {
      sessionStorage.removeItem(PendingCheckoutService.KEY);
    } catch {
      // ignore
    }
  }
}
